import { supabase } from '../supabase';
import {
  hasPermission as engineHasPermission,
  type AnyRole,
} from '../core/permissions/engine';
import { useState, useEffect } from 'react';

export interface PermissionContext {
  vaultId?: string;
  categoryId?: string;
  channelId?: string;
}

export interface PermissionUser {
  id: string;
  email?: string;
}

export type PermissionOverrideState = 'ALLOW' | 'DENY' | 'INHERIT';

const USER_ROLES_CACHE = new Map<string, { roles: AnyRole[]; at: number }>();
const CACHE_TTL_MS = 10_000;

export class PermissionService {
  /**
   * Evaluates permission for a given user in a specified context.
   * Precedence Order:
   * 1. Platform Super-Admin (chimske@gmail.com) -> ALLOW
   * 2. Server Creator (vault.created_by === userId) -> ALLOW
   * 3. Channel User Override (channel_permission_overrides for user_id) -> DENY / ALLOW
   * 4. Channel Role Overrides (channel_permission_overrides for user roles) -> DENY / ALLOW
   * 5. Category User Override (category_permission_overrides for user_id) -> DENY / ALLOW
   * 6. Category Role Overrides (category_permission_overrides for user roles) -> DENY / ALLOW
   * 7. Base Server Roles (server_roles.permission_ids & ADMINISTRATOR meta-permission)
   */
  static async has(
    user: PermissionUser | null | undefined,
    permission: string,
    context?: PermissionContext
  ): Promise<boolean> {
    if (!user || !user.id || !permission) return false;

    // 1. Super-Admin Check
    if (user.email === 'chimske@gmail.com') return true;

    if (!context?.vaultId && !context?.channelId && !context?.categoryId) return false;

    let vaultId = context?.vaultId;

    // Resolve vaultId from channel if missing
    if (!vaultId && context?.channelId) {
      const { data: channel } = await supabase
        .from('channels')
        .select('vault_id, category_id')
        .eq('id', context.channelId)
        .maybeSingle();
      if (channel) {
        vaultId = channel.vault_id;
        if (!context.categoryId && channel.category_id) {
          context = { ...context, categoryId: channel.category_id };
        }
      }
    }

    if (vaultId) {
      // 2. Server Creator Check
      const { data: vault } = await supabase
        .from('vaults')
        .select('created_by')
        .eq('id', vaultId)
        .maybeSingle();

      if (vault && vault.created_by === user.id) return true;
    }

    // 3. Channel Overrides Evaluation
    if (context?.channelId) {
      const { data: channelOverrides } = await supabase
        .from('channel_permission_overrides')
        .select('id, channel_id, role_id, user_id, allow_mask, deny_mask')
        .eq('channel_id', context.channelId);

      if (channelOverrides && channelOverrides.length > 0) {
        // Channel User Override
        const userOv = channelOverrides.find(o => o.user_id === user.id);
        if (userOv) {
          if (userOv.deny_mask?.includes(permission)) return false;
          if (userOv.allow_mask?.includes(permission)) return true;
        }

        // Channel Role Overrides
        const userRoles = await PermissionService.getUserRoles(user.id, vaultId);
        const userRoleIds = new Set(userRoles.map(r => r.id));
        const roleOverrides = channelOverrides.filter(o => o.role_id && userRoleIds.has(o.role_id));

        if (roleOverrides.length > 0) {
          const isDenied = roleOverrides.some(o => o.deny_mask?.includes(permission));
          const isAllowed = roleOverrides.some(o => o.allow_mask?.includes(permission));
          if (isDenied && !isAllowed) return false;
          if (isAllowed) return true;
        }
      }
    }

    // 4. Category Overrides Evaluation
    if (context?.categoryId || context?.channelId) {
      let catId = context?.categoryId;
      if (!catId && context?.channelId) {
        const { data: ch } = await supabase
          .from('channels')
          .select('category_id')
          .eq('id', context.channelId)
          .maybeSingle();
        catId = ch?.category_id;
      }

      if (catId) {
        const { data: categoryOverrides } = await supabase
          .from('category_permission_overrides')
          .select('id, category_id, role_id, user_id, allow_mask, deny_mask')
          .eq('category_id', catId);

        if (categoryOverrides && categoryOverrides.length > 0) {
          const userOv = categoryOverrides.find(o => o.user_id === user.id);
          if (userOv) {
            if (userOv.deny_mask?.includes(permission)) return false;
            if (userOv.allow_mask?.includes(permission)) return true;
          }

          const userRoles = await PermissionService.getUserRoles(user.id, vaultId);
          const userRoleIds = new Set(userRoles.map(r => r.id));
          const roleOverrides = categoryOverrides.filter(o => o.role_id && userRoleIds.has(o.role_id));

          if (roleOverrides.length > 0) {
            const isDenied = roleOverrides.some(o => o.deny_mask?.includes(permission));
            const isAllowed = roleOverrides.some(o => o.allow_mask?.includes(permission));
            if (isDenied && !isAllowed) return false;
            if (isAllowed) return true;
          }
        }
      }
    }

    // 5. Base Server Roles Evaluation
    if (vaultId) {
      const userRoles = await PermissionService.getUserRoles(user.id, vaultId);
      return engineHasPermission(userRoles, permission);
    }

    return false;
  }

  public static async getUserRoles(userId: string, vaultId?: string): Promise<AnyRole[]> {
    if (!vaultId || !userId) return [];
    const cacheKey = `${userId}:${vaultId}`;
    const cached = USER_ROLES_CACHE.get(cacheKey);
    if (cached && Date.now() - cached.at < CACHE_TTL_MS) {
      return cached.roles;
    }

    const [{ data: allRoles }, { data: memberRoles }] = await Promise.all([
      supabase.from('server_roles').select('id, name, position, vault_id, role_kind, permission_ids').eq('vault_id', vaultId),
      supabase.from('server_member_roles').select('role_id').eq('vault_id', vaultId).eq('user_id', userId),
    ]);
    if (!allRoles || !memberRoles) return [];
    const assigned = new Set(memberRoles.map(r => r.role_id));
    const roles = (allRoles as AnyRole[]).filter(r => assigned.has(r.id));
    USER_ROLES_CACHE.set(cacheKey, { roles, at: Date.now() });
    return roles;
  }

  public static clearCache() {
    USER_ROLES_CACHE.clear();
  }
}

export function usePermission(permission: string, context?: PermissionContext): boolean {
  const [hasPerm, setHasPerm] = useState<boolean>(false);

  useEffect(() => {
    let isMounted = true;
    async function checkPermission() {
      const { data: { session } } = await supabase.auth.getSession();
      const user = session?.user ?? null;
      if (!user) {
        if (isMounted) setHasPerm(false);
        return;
      }
      const allowed = await PermissionService.has(user, permission, context);
      if (isMounted) setHasPerm(allowed);
    }
    void checkPermission();
    return () => { isMounted = false; };
  }, [permission, context?.vaultId, context?.categoryId, context?.channelId]);

  return hasPerm;
}
